import { useCallback } from 'react';

import { billingApi, billingEncountersApi } from '../store/api';

/**
 * Warming the workbench's first fetch wave, for whoever knows a claim is
 * about to be opened.
 *
 * Extracted from `useWorklistRowPrefetch` (GDN-3201) when the claim queue
 * (GDN-3322) needed the same prefetches from a second place. The row
 * hook's own guards — hover-capable pointers only, once per row per mount —
 * stayed with it, because they describe HOVER; they are not properties of
 * warming a cache. The queue's arrival prefetch has its own reasons to fire
 * and its own reasons to skip.
 *
 * Certification waits for the workbench to read claim eligibility; queue
 * targets only carry IDs.
 *
 * The cache-key rule that travels with the prefetch: the
 * workspace arg carries BOTH ids, because the workbench subscribes with both
 * (`useClaimWorkspaceModel`) and RTK Query's cache key IS the serialized arg.
 * An encounterId-only prefetch warms an entry the workbench never reads — a
 * request that costs everything and saves nothing.
 */

/** Don't re-request data fresher than this. */
export const CLAIM_WORKSPACE_PREFETCH_TTL_SECONDS = 30;

export interface ClaimWorkspacePrefetchTarget {
  claimId: string;
  /** Null for a row with no encounter (imported claims) — the claim
   *  record still warms; the workspace GET cannot. */
  encounterId: string | null;
}

export function useClaimWorkspacePrefetch(): (target: ClaimWorkspacePrefetchTarget) => void {
  const prefetchWorkspace = billingEncountersApi.usePrefetch(
    'getV2ProviderBillingEncountersByEncounterIdClaimWorkspace',
    { ifOlderThan: CLAIM_WORKSPACE_PREFETCH_TTL_SECONDS },
  );
  const prefetchClaim = billingApi.usePrefetch('getV2ProviderClaimsByClaimId', {
    ifOlderThan: CLAIM_WORKSPACE_PREFETCH_TTL_SECONDS,
  });

  return useCallback(
    ({ claimId, encounterId }: ClaimWorkspacePrefetchTarget) => {
      /* The workspace's cache key needs both IDs used by the direct route. */
      prefetchClaim({ claimId });
      if (encounterId) prefetchWorkspace({ claimId, encounterId });
    },
    [prefetchClaim, prefetchWorkspace],
  );
}
